import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { CommonModule } from "@angular/common";

/**
 * EVA Switch - Composant 100% custom
 *
 * Toggle switch utilisant les tokens EVA (pas de PrimeNG).
 * Supporte label, description, loading state.
 *
 * @example
 * // Switch simple
 * <eva-switch label="Notifications" [(checked)]="enableNotifications"></eva-switch>
 *
 * // Switch avec description
 * <eva-switch
 *   label="Mode sombre"
 *   description="Activer le thème sombre pour l'interface"
 *   [(checked)]="darkMode"
 * ></eva-switch>
 *
 * // Switch avec labels on/off personnalisés
 * <eva-switch
 *   label="Statut"
 *   onLabel="Actif"
 *   offLabel="Inactif"
 *   [(checked)]="isActive"
 * ></eva-switch>
 */
@Component({
  selector: "eva-switch",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./eva-switch.component.html",
  styleUrls: ["./eva-switch.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EvaSwitchComponent),
      multi: true,
    },
  ],
})
export class EvaSwitchComponent implements ControlValueAccessor {
  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Label principal */
  @Input() label: string = "";

  /** Description additionnelle */
  @Input() description: string = "";

  /** Désactive le switch */
  @Input() disabled: boolean = false;

  /** État de chargement */
  @Input() loading: boolean = false;

  /** Taille du switch */
  @Input() size: "small" | "medium" | "large" = "medium";

  /** Label affiché quand ON */
  @Input() onLabel: string = "";

  /** Label affiché quand OFF */
  @Input() offLabel: string = "";

  /** Position du label : avant ou après le switch */
  @Input() labelPosition: "before" | "after" = "after";

  /** ID unique pour le switch (auto-généré si non fourni) */
  @Input() inputId: string = `eva-switch-${Math.random().toString(36).substr(2, 9)}`;

  // ==========================================================================
  // Outputs
  // ==========================================================================

  /** Émis quand l'état change — pour le two-way binding [(checked)] */
  @Output() checkedChange = new EventEmitter<boolean>();

  // ==========================================================================
  // Internal state
  // ==========================================================================

  _checked: boolean = false;
  isFocused: boolean = false;

  // ==========================================================================
  // Two-way binding [(checked)]
  // ==========================================================================

  @Input()
  get checked(): boolean {
    return this._checked;
  }
  set checked(val: boolean) {
    if (val !== this._checked) {
      this._checked = val;
      this.onChange(val);
      this.checkedChange.emit(val);
    }
  }

  // ==========================================================================
  // ControlValueAccessor
  // ==========================================================================

  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: boolean): void {
    this._checked = !!value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ==========================================================================
  // Computed
  // ==========================================================================

  get isDisabled(): boolean {
    return this.disabled || this.loading;
  }

  get currentStateLabel(): string {
    if (this._checked && this.onLabel) return this.onLabel;
    if (!this._checked && this.offLabel) return this.offLabel;
    return "";
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  toggle(): void {
    if (this.isDisabled) return;

    this._checked = !this._checked;
    this.onChange(this._checked);
    this.checkedChange.emit(this._checked);
    this.onTouched();
  }

  handleFocus(): void {
    this.isFocused = true;
  }

  handleBlur(): void {
    this.isFocused = false;
    this.onTouched();
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      this.toggle();
    }
  }
}
